import { apiClient } from '@/lib/api';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

interface ReservationSummary {
  listingName: string;
  arrivalDate: string;
  departureDate: string;
  arrivalTime?: string | null;
  departureTime?: string | null;
  timezone?: string | null;
  guestName?: string;
  listingAvatarUrl?: string | null;
  googleMapsApiKey?: string;
}

interface ReservationContextType {
  summary: ReservationSummary | null;
  loaded: boolean;
  error: string | null;
}

const ReservationContext = createContext<ReservationContextType | undefined>(undefined);

interface ReservationProviderProps {
  children: React.ReactNode;
}

export function ReservationProvider({ children }: ReservationProviderProps) {
  const { isAuthenticated, token } = useAuth();
  const [summary, setSummary] = useState<ReservationSummary | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      // Check if we're in preview mode (public guidebook preview)
      const params = new URLSearchParams(window.location.search);
      const previewListingId = params.get('listingId');

      if (previewListingId) {
        // Public preview mode - no auth required
        try {
          setError(null);
          const data = (await apiClient.get(
            `/v1/reservation/summary-preview?listingId=${previewListingId}`
          )) as ReservationSummary;
          setSummary(data);
          setLoaded(true);
        } catch (err) {
          console.error('Error fetching preview summary:', err);
          setError('Failed to load preview details');
          setLoaded(true);
        }
        return;
      }

      // Authenticated mode
      if (!isAuthenticated || !token) {
        setSummary(null);
        setLoaded(false);
        setError(null);
        return;
      }

      try {
        setError(null);
        const data = (await apiClient.get('/v1/reservation/summary')) as ReservationSummary;
        setSummary(data);
        setLoaded(true);
      } catch (err) {
        console.error('Error fetching reservation summary:', err);
        setError('Failed to load reservation details');
        setLoaded(true);
      }
    };

    fetchSummary();
  }, [isAuthenticated, token]);

  return <ReservationContext.Provider value={{ summary, loaded, error }}>{children}</ReservationContext.Provider>;
}

export function useReservation(): ReservationContextType {
  const context = useContext(ReservationContext);
  if (context === undefined) {
    throw new Error('useReservation must be used within a ReservationProvider');
  }
  return context;
}
